import { dinero, fecha, useTraduccion } from '@tallerya/i18n';
import { PanelLateral } from '@tallerya/ui';
import { textoDeEntrada, type Campos } from '../../componentes/historial/cambios';
import { LineaDeTiempo } from '../../componentes/historial/LineaDeTiempo';
import { useMoneda } from '../../datos/useMoneda';
import { diaLocal, useHistorialGasto } from './datos';

// El panel «Historial» de la ficha del gasto (#189), con la línea de tiempo
// común. Lo de acá es cómo se lee cada cambio:
//
// - **El monto, con cifras solo para quien puede ver montos**: sin el permiso
//   llega sin valor y sale «Cambió: monto». Lo decide el servidor, no esta pantalla.
// - **El proveedor, la orden y la sucursal llegan por su nombre**, y un proveedor
//   eliminado lleva al lado la etiqueta «Eliminado».
// - **El pago va en su propio renglón**, con el día en que se pagó: la marca no
//   siempre se pone el mismo día.
// - **El comprobante se llama comprobante**, sea foto o PDF (#195), y sin el
//   nombre del archivo.

export function PanelHistorial({
  gastoId,
  abierto,
  onCerrar,
}: {
  gastoId: string;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const { t, idioma } = useTraduccion();
  const moneda = useMoneda();
  const consulta = useHistorialGasto(gastoId, abierto);
  const dia = (texto: string) => fecha(diaLocal(texto), idioma);

  const campos: Campos = {
    description: { etiqueta: t('admin.gastos.formulario.descripcion') },
    amount: {
      etiqueta: t('admin.gastos.formulario.monto'),
      // Con la moneda del taller, o con dos decimales mientras llega.
      valor: (monto) => (moneda ? dinero(Number(monto), moneda, idioma) : Number(monto).toFixed(2)),
      vacio: t('admin.gastos.sinMonto'),
    },
    supplier_id: {
      etiqueta: t('admin.gastos.formulario.proveedor'),
      vacio: t('admin.gastos.formulario.sinProveedor'),
    },
    document_number: { etiqueta: t('admin.gastos.formulario.factura') },
    purchased_on: { etiqueta: t('admin.gastos.formulario.fecha'), valor: dia },
    // Sin «Del taller» para el nulo: también lo es una orden que todavía no tiene folio.
    service_order_id: { etiqueta: t('admin.gastos.listado.columna.orden') },
    location_id: { etiqueta: t('admin.gastos.ficha.sucursal') },
    notes: { etiqueta: t('admin.gastos.formulario.notas') },
    // «Pagado el 5 oct 2026» bajo «Marcado pagado»; al quitar la marca, el día que tenía.
    paid_on: {
      frase: (cambio) =>
        cambio.to
          ? t('admin.gastos.historial.pagadoEl', { fecha: dia(cambio.to) })
          : cambio.from
            ? t('admin.gastos.historial.estabaPagado', { fecha: dia(cambio.from) })
            : '',
    },
    attachment_added: { frase: () => t('admin.gastos.historial.comprobanteAgregado') },
    attachment_removed: { frase: () => t('admin.gastos.historial.comprobanteEliminado') },
  };

  return (
    <PanelLateral abierto={abierto} onCerrar={onCerrar} titulo={t('admin.historial.titulo')} info={t('admin.gastos.historial.info')}>
      <LineaDeTiempo consulta={consulta} texto={(entrada) => textoDeEntrada(entrada, campos, t)} />
    </PanelLateral>
  );
}
